import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  Button,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Icon,
  Input,
  Stack,
} from '@eluve/components';

// Email-only registration schema
const registrationFormSchema = z.object({
  email: z.string().email('Enter a valid email'),
  agreeToTerms: z.boolean().refine((val) => val === true, {
    message: 'You must agree to the terms to continue',
  }),
});

export type RegistrationFormData = z.infer<typeof registrationFormSchema>;

export interface MagicLinkLoginControlsProps {
  email: string;
  isBusy?: boolean;
  onRegistrationSubmit: (data: RegistrationFormData) => Promise<void>;
  onAgreeToTermsChange?: (agreed: boolean) => void;
}

export const MagicLinkLoginControls: React.FC<MagicLinkLoginControlsProps> = ({
  email,
  isBusy = false,
  onRegistrationSubmit,
  onAgreeToTermsChange,
}) => {
  const registrationForm = useForm<RegistrationFormData>({
    resolver: zodResolver(registrationFormSchema),
    defaultValues: { email, agreeToTerms: false },
  });

  // Keep parent-provided email and form in sync if prop changes
  useEffect(() => {
    registrationForm.setValue('email', email);
  }, [email, registrationForm]);

  useEffect(() => {
    const subscription = registrationForm.watch((value, { name }) => {
      if (name === 'agreeToTerms' && onAgreeToTermsChange) {
        onAgreeToTermsChange(Boolean(value.agreeToTerms));
      }
    });
    return () => subscription.unsubscribe();
  }, [registrationForm, onAgreeToTermsChange]);

  return (
    <Stack align="center" gap={2}>
      <Form {...registrationForm}>
        <form
          onSubmit={registrationForm.handleSubmit(onRegistrationSubmit)}
          className="w-full space-y-5"
        >
          <FormField
            control={registrationForm.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs text-text-tertiary">
                  Email
                </FormLabel>
                <FormControl>
                  <div className="relative mt-0">
                    <div className="pointer-events-none absolute top-1/2 left-2 mr-0.5 -translate-y-1/2 rounded-sm bg-bg-gray-subtle p-1.5">
                      <Icon
                        name="Mail"
                        size="xl"
                        className="size-4 text-text-tertiary"
                      />
                    </div>
                    <Input
                      placeholder="you@company.com"
                      {...field}
                      className="pl-11"
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={registrationForm.control}
            name="agreeToTerms"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-y-0 space-x-3 pt-2">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className="text-sm font-normal">
                    By signing up, you agree to our{' '}
                    <a
                      className="underline"
                      href="https://www.eluve.com/terms"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Terms of Service,
                    </a>{' '}
                    <a
                      className="underline"
                      href="https://www.eluve.com/privacy-policy"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Privacy Policy
                    </a>{' '}
                    and, if applicable, our{' '}
                    <a
                      className="underline"
                      href="https://www.eluve.com/business-associate-agreement"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Business Associate Agreement
                    </a>
                  </FormLabel>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          <Button
            disabled={isBusy || !registrationForm.watch('agreeToTerms')}
            fullWidth
            type="submit"
          >
            {isBusy ? 'Creating Account...' : 'Create Account'}
          </Button>
        </form>
      </Form>
    </Stack>
  );
};
